import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "../setup";
// Define a service using a base URL and expected endpoints
export const foodStationOptionsApi = createApi({
  reducerPath: "foodStationOptionsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "FoodStationOptions",
    "FoodStationOptionGroup",
    "FoodStationDeletedOptions",
  ],
  endpoints: (builder) => ({
    getStationOptionInfo: builder.query({
      query: ({ id, subDomain }) => {
        return {
          url: `/station-option/${id}/info`,
          method: "GET",
          params: { subdomain: subDomain },
        };
      },
      keepUnusedDataFor: 3600,
    }),
    getStationOptionsList: builder.query({
      query: ({ id, subDomain }) => {
        return {
          url: `/station-option/${id}/list`,
          method: "GET",
          params: { subdomain: subDomain },
        };
      },
      transformResponse: (response) => {
        return response.map((a) => ({
          group: a.group,
          items: a.items.sort((a, b) => a.order - b.order),
        }));
      },
      providesTags: (result) => {
        return result
          ? [
              ...result.map(({ id }) => ({ type: "FoodStationOptions", id })),
              { type: "FoodStationOptions", id: "LIST" },
            ]
          : [{ type: "FoodStationOptions", id: "LIST" }];
      },
    }),
    saveOptionsOrder: builder.mutation({
      query: ({ payload, subDomain }) => {
        return {
          url: `/station-option/update-list`,
          method: "POST",
          body: payload,
          params: { subdomain: subDomain },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    saveGroupOrder: builder.mutation({
      query: ({ payload, subDomain }) => {
        return {
          url: `/station-option/group/update-list`,
          method: "POST",
          body: payload,
          params: { subdomain: subDomain },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    getOptionGroupData: builder.query({
      query: ({ id, subdomain }) => {
        return {
          url: `/station-option/group/export/${id}`,
          method: "GET",
          params: { subdomain },
        };
      },
      providesTags: (result, error, id) => [
        { type: "FoodStationOptionGroup", id },
      ],
    }),
    importOptionGroup: builder.mutation({
      query: (payload) => {
        return {
          url: `/station-option/group/import`,
          method: "POST",
          body: { ...payload },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    createOptionGroup: builder.mutation({
      query: (payload) => {
        return {
          url: `/station-option/group/create`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    updateOptionGroup: builder.mutation({
      query: ({ id, data, subDomain }) => {
        return {
          url: `/station-option/group/${id}`,
          method: "PATCH",
          body: { ...data },
          params: { subdomain: subDomain },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    deleteOptionGroup: builder.mutation({
      query: ({ id, subDomain }) => {
        return {
          url: `/station-option/group/${id}`,
          method: "DELETE",
          params: { subdomain: subDomain },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    createOption: builder.mutation({
      query: ({ data, subDomain }) => {
        return {
          url: `/station-option/create`,
          method: "POST",
          body: { ...data },
          params: { subdomain: subDomain },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    updateOption: builder.mutation({
      query: ({ id, data, subDomain }) => {
        return {
          url: `/station-option/${id}`,
          method: "PATCH",
          body: { ...data },
          params: { subdomain: subDomain },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    deleteOption: builder.mutation({
      query: ({ id, subDomain }) => {
        return {
          url: `/station-option/${id}`,
          method: "DELETE",
          params: { subdomain: subDomain },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    uploadImage: builder.mutation({
      query: ({ id, subdomain, data, groupId, optionId, imageType }) => {
        const formData = new FormData();
        formData.append("file", data);
        formData.append("group_id", groupId);
        formData.append("option_id", optionId);
        formData.append("type", imageType);
        return {
          url: `/station-option/image/upload/${id}`,
          method: "POST",
          body: formData,
          params: { subdomain: subdomain },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    getStationDeletedOptionsList: builder.query({
      query: ({ id, subDomain }) => {
        return {
          url: `/station-option/${id}/deleted`,
          method: "GET",
          params: { subdomain: subDomain },
        };
      },
      transformResponse: (response) => {
        return response.map((a) => ({
          group: { ...a.group, isDeleted: true },
          items: a.items
            .sort((a, b) => a.order - b.order)
            .map((item) => ({ ...item, isDeleted: true })),
        }));
      },
      providesTags: (result) => {
        return result
          ? [
              ...result.map(({ id }) => ({
                type: "FoodStationDeletedOptions",
                id,
              })),
              { type: "FoodStationDeletedOptions", id: "LIST" },
            ]
          : [{ type: "FoodStationDeletedOptions", id: "LIST" }];
      },
    }),
    recoverOptionGroup: builder.mutation({
      query: ({ id, subDomain }) => {
        return {
          url: `/station-option/group/recover/${id}`,
          method: "PATCH",
          params: { subdomain: subDomain },
        };
      },
      invalidatesTags: [{ type: "FoodStationOptions", id: "LIST" }],
    }),
    permanentlyDeleteOptionGroup: builder.mutation({
      query: ({ id, subDomain }) => {
        return {
          url: `/station-option/group/permanently/${id}`,
          method: "DELETE",
          params: { subdomain: subDomain },
        };
      },
    }),
    permanentlyDeleteOption: builder.mutation({
      query: ({ id, subDomain }) => {
        return {
          url: `/station-option/permanently/${id}`,
          method: "DELETE",
          params: { subdomain: subDomain },
        };
      },
    }),
    recoverOption: builder.mutation({
      query: ({ id, subDomain }) => {
        return {
          url: `/station-option/recover/${id}`,
          method: "PATCH",
          params: { subdomain: subDomain },
        };
      },
    }),
  }),
});

export const {
  useGetStationOptionsListQuery,
  useLazyGetStationOptionsListQuery,
  useGetStationOptionInfoQuery,
  useLazyGetStationOptionInfoQuery,
  useSaveOptionsOrderMutation,
  useSaveGroupOrderMutation,
  useCreateOptionGroupMutation,
  useUpdateOptionGroupMutation,
  useDeleteOptionGroupMutation,
  useCreateOptionMutation,
  useUpdateOptionMutation,
  useDeleteOptionMutation,
  useUploadImageMutation,
  useLazyGetOptionGroupDataQuery,
  useImportOptionGroupMutation,
  useLazyGetStationDeletedOptionsListQuery,
  usePermanentlyDeleteOptionGroupMutation,
  useRecoverOptionGroupMutation,
  useRecoverOptionMutation,
  usePermanentlyDeleteOptionMutation,
} = foodStationOptionsApi;
